import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { PermissionsService } from '../permissions.service';
import { isPermissionDeniedError } from '../access-errors';
import { NotificationService } from '../handlers/notification.service';
import { deniedRedirect } from '@guards/permission.guard';

export const PERMISSIONS_HASH_HEADER = 'X-Permissions-Hash';

/**
 * Keeps the session in sync with the API:
 * - `X-Permissions-Hash` different from the stored one -> refresh `/me`.
 *   (The header must be exposed through CORS; when the browser hides it the
 *   value is null and this part simply does nothing.)
 * - 403 PERMISSION_DENIED / OWNER_ONLY -> refresh `/me`, never logout.
 * After a refresh, if the current screen is no longer allowed, it leaves it.
 */
@Injectable()
export class PermissionsInterceptor implements HttpInterceptor {
  constructor(
    private readonly permissions: PermissionsService,
    private readonly notifications: NotificationService,
    private readonly injector: Injector,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const isApi = request.url.startsWith(environment.apiUrl);
    const isMe = request.url.endsWith('/user/account/me');

    return next.handle(request).pipe(
      tap((event) => {
        if (!isApi || isMe || !(event instanceof HttpResponse)) return;
        const incoming = event.headers.get(PERMISSIONS_HASH_HEADER);
        const current = this.permissions.hash;
        if (incoming && current && incoming !== current) {
          this.permissions.refresh().subscribe((ok) => {
            if (ok) {
              this.notifications.showToastWarning(
                'Tu acceso cambió. Actualizamos la pantalla.',
              );
              this.leaveIfDenied();
            }
          });
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (isApi && isPermissionDeniedError(error)) {
          this.permissions.refresh().subscribe((ok) => {
            if (ok) this.leaveIfDenied();
          });
        }
        return throwError(() => error);
      }),
    );
  }

  private leaveIfDenied(): void {
    // Resolved lazily: the Router is not needed until a refresh lands.
    const router = this.injector.get(Router);
    const target = deniedRedirect(router, this.permissions);
    if (target) router.navigateByUrl(target);
  }
}
